import { Deferred } from '@inertiajs/react'
import { Shield } from 'lucide-react'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import { Section } from '@/components/design-system/Section'
import {
  ChipsSkeleton, ListRowsSkeleton, StatCardsSkeleton, TableSkeleton,
} from '@/frontend/components/skeletons/ContentSkeletons'
import KpiRow from './components/KpiRow'
import FleetOverviewSection from './components/FleetOverviewSection'
import TopOrganizationsSection from './components/TopOrganizationsSection'
import RecentActivitySection from './components/RecentActivitySection'
import IntegrationHealthSection from './components/IntegrationHealthSection'

interface Props {
  kpis: { totalUsers: number; totalOrganizations: number; totalDigitalWorkers: number; trialOrganizations: number; payingOrganizations: number; agentsWithPlan: number; monthlyRevenue: number }
  sessionStats: { activeSessions: number; totalTokens: number; totalCost: number }
  fleetOverview: { byStatus: Record<string, number>; byProfile: Record<string, number>; recentWorkers: Array<{ id: string; name: string; organizationName: string; provisioningStatus: string; createdAt: string }> }
  topOrganizations: Array<{ id: string; name: string; slug: string; sessionsCount: number; totalTokens: number; lastActivityAt: string | null }>
  recentAuditLogs: Array<{ id: string; description: string; actor_name: string | null; organization_name: string; worker_name: string | null; worker_path: string | null; created_at: string }>
  integrationHealth: { byProvider: Record<string, number>; byStatus: Record<string, number>; expiredCount: number; totalCount: number }
}

// Fallback do KpiRow com a mesma forma das duas grades de StatCards (SZ-1589).
function KpiRowSkeleton() {
  return (
    <div className="space-y-3">
      <StatCardsSkeleton count={6} />
      <StatCardsSkeleton count={4} gridClassName="grid-cols-2 md:grid-cols-4" />
    </div>
  )
}

function DashboardIndex({ kpis, sessionStats, fleetOverview, topOrganizations, recentAuditLogs, integrationHealth }: Props) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="h-6 w-6 text-red-500" />
        <div>
          <h2 className="text-2xl font-bold text-foreground">Visão Geral</h2>
          <p className="text-sm text-muted-foreground">Painel administrativo do Command Center</p>
        </div>
      </div>

      {/* Both `kpis` and `sessionStats` are InertiaRails.defer-ed by the */}
      {/* controller. KpiRow assumes `kpis` is defined at render time, so we */}
      {/* gate the whole row on `kpis` arriving first; once it does, the */}
      {/* inner Deferred handles `sessionStats` with a partial-load fallback. */}
      <Deferred data="kpis" fallback={<KpiRowSkeleton />}>
        <Deferred data="sessionStats" fallback={<KpiRow kpis={kpis} sessionStats={null} />}>
          <KpiRow kpis={kpis} sessionStats={sessionStats} />
        </Deferred>
      </Deferred>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Deferred data="fleetOverview" fallback={<Section title="Visão da Frota"><ListRowsSkeleton rows={4} /></Section>}>
          <FleetOverviewSection data={fleetOverview} />
        </Deferred>
        <Deferred data="topOrganizations" fallback={<Section title="Top Organizações"><TableSkeleton rows={5} /></Section>}>
          <TopOrganizationsSection organizations={topOrganizations} />
        </Deferred>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Deferred data="recentAuditLogs" fallback={<Section title="Atividade Recente"><ListRowsSkeleton rows={5} /></Section>}>
          <RecentActivitySection logs={recentAuditLogs} />
        </Deferred>
        <Deferred data="integrationHealth" fallback={<Section title="Saúde das Integrações"><ChipsSkeleton count={8} /></Section>}>
          <IntegrationHealthSection data={integrationHealth} />
        </Deferred>
      </div>
    </div>
  )
}

DashboardIndex.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default DashboardIndex
